import type { ReactNode } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import type { RootLoaderData } from '../App';
import { NotFoundPage } from '../pages/NotFoundPage';
import { useMountedAddons } from './useMountedAddons';

/**
 * Wraps an addon-owned route so visitors to a disabled or uninstalled addon
 * see the 404 page instead of a half-broken UI. The runtime's `isEnabled`
 * lookup is backed by `/addons/installations`, so uninstalling triggers an
 * immediate refetch and the guard flips to "not found" without a reload.
 */
export function AddonRouteGuard({
  addonId,
  children,
}: {
  addonId: string;
  children: ReactNode;
}) {
  const data = useRouteLoaderData('root') as RootLoaderData | undefined;
  const { isEnabled, isLoading } = useMountedAddons(data?.tenant.id ?? '');

  if (isLoading) {
    return (
      <div className="flex h-full min-h-[240px] items-center justify-center" aria-busy="true">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-border)] border-t-[var(--color-accent)]" />
      </div>
    );
  }

  if (!isEnabled(addonId)) {
    return <NotFoundPage />;
  }

  return <>{children}</>;
}
